import type { QuestionDefinition } from "@reno-estimator/core";
import { BASEMENT_SIZE_RANGES } from "../basement-size-ranges.js";

/**
 * Basement's question set (Phase 8, revised in Phase 9).
 *
 * Phase 8 originally pointed every one of these questions at BOTH
 * "basement" and "basement_suite" - i.e. a basement suite was just a
 * finished basement with a different label. Phase 9 undoes that: a
 * basement suite is a materially different project (a self-contained
 * secondary dwelling unit, with its own kitchen/bathroom/egress/fire-
 * separation/electrical/plumbing-capacity/sound-separation/permit
 * requirements - see docs/basement-suite-estimator.md), so it now gets
 * its own question set in ./basement-suite.ts rather than reusing this
 * one unchanged.
 *
 * What's still genuinely shared (exported below for basement-suite.ts to
 * reuse, rather than duplicated) is the subset that really is identical
 * for a plain finished basement and a basement suite: how big the space
 * is, its current condition (which drives envelope pricing the same way
 * either way), ceiling/flooring tiers, HVAC scope, and the two optional
 * extras (fireplace, built-ins). Room composition, bathroom, kitchen,
 * egress, and soundproofing are NOT shared - a suite's version of each of
 * those is a distinct question with different options/requirements, see
 * ./basement-suite.ts.
 */

export const BASEMENT_SIZE_RANGE_OPTIONS = BASEMENT_SIZE_RANGES.map((r) => ({ id: r.id, label: r.label }));

export const basementSizeRangeQuestion: QuestionDefinition = {
  id: "basement_size_range",
  projectTypes: ["basement", "basement_suite"],
  inputType: "single_select",
  prompt: "How large is the basement?",
  helpText: "A rough range is fine - you can enter an exact square footage instead if you know it.",
  required: false,
  options: BASEMENT_SIZE_RANGE_OPTIONS,
  order: 15
};

export const basementCurrentConditionQuestion: QuestionDefinition = {
  id: "basement_current_condition",
  projectTypes: ["basement", "basement_suite"],
  inputType: "single_select",
  prompt: "What's the current condition of the basement?",
  helpText: "This materially changes what work is actually needed - see the note on your results if a site inspection is recommended.",
  required: true,
  options: [
    { id: "unfinished", label: "Unfinished", description: "Bare studs/concrete - full envelope (framing, insulation, drywall) is needed." },
    { id: "partially_finished", label: "Partially finished", description: "Some framing/insulation/drywall already exists." },
    { id: "previously_finished_renovating", label: "Previously finished, now renovating", description: "The space was already a finished basement; you're updating or redoing it." }
  ],
  order: 190
};

export const basementCeilingTypeQuestion: QuestionDefinition = {
  id: "basement_ceiling_type",
  projectTypes: ["basement", "basement_suite"],
  inputType: "single_select",
  prompt: "What ceiling type are you planning?",
  required: false,
  options: [
    { id: "drywall", label: "Drywall ceiling" },
    { id: "drop_ceiling", label: "Drop / suspended ceiling", description: "Easier access to ductwork/wiring above." },
    { id: "exposed_finished", label: "Exposed, finished look", description: "Painted joists/ductwork left exposed rather than covered." }
  ],
  order: 230
};

export const basementFlooringTypeQuestion: QuestionDefinition = {
  id: "basement_flooring_type",
  projectTypes: ["basement", "basement_suite"],
  inputType: "single_select",
  prompt: "What's happening with flooring?",
  required: false,
  options: [
    { id: "basic", label: "Basic", description: "E.g. builder-grade laminate or carpet tile." },
    { id: "mid_range", label: "Mid-range", description: "E.g. engineered hardwood or vinyl plank." },
    { id: "premium", label: "Premium", description: "E.g. premium LVP, heated flooring substrate, or higher-end tile." }
  ],
  order: 235
};

export const basementHvacScopeQuestion: QuestionDefinition = {
  id: "basement_hvac_scope",
  projectTypes: ["basement", "basement_suite"],
  inputType: "single_select",
  prompt: "What's needed for heating/cooling the basement?",
  required: false,
  options: [
    { id: "not_needed", label: "Not needed", description: "Existing heating/cooling is sufficient." },
    { id: "extend_existing", label: "Extend existing system", description: "Add ductwork/registers or a supplemental unit off the existing system." },
    { id: "dedicated_zone", label: "Dedicated zone/system", description: "A separate zone, mini-split, or standalone system for the basement." }
  ],
  order: 240
};

export const basementFireplaceQuestion: QuestionDefinition = {
  id: "basement_fireplace",
  projectTypes: ["basement", "basement_suite"],
  inputType: "boolean",
  prompt: "Add a fireplace?",
  required: false,
  order: 250
};

export const basementBuiltInsQuestion: QuestionDefinition = {
  id: "basement_built_ins",
  projectTypes: ["basement", "basement_suite"],
  inputType: "boolean",
  prompt: "Add built-in cabinetry/shelving?",
  required: false,
  order: 255
};

/** Questions shared between a plain finished basement and a basement suite - see the file-level doc comment above. */
export const SHARED_BASEMENT_QUESTIONS: QuestionDefinition[] = [
  basementSizeRangeQuestion,
  basementCurrentConditionQuestion,
  basementCeilingTypeQuestion,
  basementFlooringTypeQuestion,
  basementHvacScopeQuestion,
  basementFireplaceQuestion,
  basementBuiltInsQuestion
];

/** Questions specific to a plain finished basement (not a secondary suite) - see ./basement-suite.ts for the suite equivalents. */
const BASEMENT_ONLY_QUESTIONS: QuestionDefinition[] = [
  {
    id: "basement_rooms",
    projectTypes: ["basement"],
    inputType: "multi_select",
    prompt: "What rooms/spaces should the finished basement include?",
    helpText: "Bathroom and kitchen/wet bar are covered by their own questions below - no need to select them here.",
    required: false,
    options: [
      { id: "rec_area", label: "Open recreation area", description: "Family/rec room, home theatre, play space, etc." },
      { id: "bedroom", label: "Bedroom" },
      { id: "office", label: "Home office" },
      { id: "storage", label: "Storage / utility area" },
      { id: "other", label: "Other room(s)" }
    ],
    order: 195
  },
  {
    id: "basement_other_rooms_description",
    projectTypes: ["basement"],
    inputType: "text",
    prompt: "Briefly describe the other room(s)",
    required: false,
    // Phase 20 audit fix: this had no displayIf at all, so it showed up regardless of
    // whether "Other room(s)" was actually selected in basement_rooms above.
    displayIf: [{ questionId: "basement_rooms", equalsAnyOf: ["other"] }],
    order: 196
  },
  {
    id: "basement_bathroom_scope",
    projectTypes: ["basement"],
    inputType: "single_select",
    prompt: "What's happening with a bathroom?",
    required: false,
    options: [
      { id: "none", label: "No bathroom in the basement" },
      { id: "existing_renovation", label: "Renovating an existing basement bathroom" },
      { id: "new_bathroom", label: "Adding a brand-new bathroom" }
    ],
    order: 200
  },
  {
    id: "basement_kitchen_scope",
    projectTypes: ["basement"],
    inputType: "single_select",
    prompt: "What's happening with a kitchen or wet bar?",
    required: false,
    options: [
      { id: "none", label: "None" },
      { id: "wet_bar", label: "Wet bar", description: "A sink and small counter/storage - no cooking appliances." },
      { id: "kitchenette", label: "Kitchenette", description: "Compact cooking setup - e.g. mini fridge, microwave/hot plate, small counter." },
      { id: "full_kitchen", label: "Full kitchen", description: "A complete second kitchen - full-size appliances and cabinetry." }
    ],
    order: 210
  },
  {
    id: "basement_egress_status",
    projectTypes: ["basement"],
    inputType: "single_select",
    prompt: "What's the egress situation?",
    helpText: "Most Canadian building codes require a compliant egress window for a legal bedroom. If you're not sure, choose \"Possible new/modified requirement\" - your results will flag this for verification.",
    required: false,
    options: [
      { id: "not_required", label: "Not required", description: "No bedroom or other space requiring egress." },
      { id: "existing_compliant", label: "Existing compliant egress", description: "An egress window/door already meets code." },
      { id: "new_or_modified_required", label: "Possible new/modified requirement", description: "A new or enlarged egress window may be needed." }
    ],
    order: 220
  },
  {
    id: "basement_soundproofing",
    projectTypes: ["basement"],
    inputType: "boolean",
    prompt: "Add soundproofing?",
    helpText: "E.g. for a home theatre, music room, or bedroom below shared living spaces.",
    required: false,
    order: 260
  }
];

export const basementQuestions: QuestionDefinition[] = [...SHARED_BASEMENT_QUESTIONS, ...BASEMENT_ONLY_QUESTIONS];
